/**
 * Docked-panel density decisions (owner feedback round, Miles 2026-08-01).
 *
 * The docked right panel (~420px pane on desktop) was cramming at BOTH ends:
 * the header stacked title + routine meta + a TRUNCATED Pause chip + "JUST
 * YOU" + a five-button icon rail into ~340px of content width, and the
 * thread chip strip crowded directly against the composer. The directive is
 * width-aware DENSITY, not uniform shrinking: docked mode drops what is
 * noise at that width and folds secondary actions into an overflow menu,
 * while fullscreen (and the wide floating pane) keep their current layout.
 *
 * These are the PURE mode/width decisions, extracted so they are directly
 * unit-testable and shared between the host render sites (GlobalChatSystem
 * consults exactly these — source-pinned in dockedHeaderDensity.test.tsx /
 * dockedFooterDensity.test.tsx). They are mode-driven (docked vs fullscreen),
 * never page-specific, so the behavior is identical on every page that
 * mounts the one persistent chat surface (chatSurfaceUniformity pins that).
 */
import { isGroupParticipantCount } from '../../services/groupThreads/senderIdentity';
import { formatMessageTime } from './utils';

/**
 * The ONE definition of "the chat is the docked right panel": docked flag on
 * and not fullscreen (the reducer clears the dock flag entering fullscreen —
 * this is belt-and-suspenders, same as useChatModeDocumentSignal's caller).
 */
export function isDockedPanelMode(isChatDockedRight: boolean, isChatFullScreen: boolean): boolean {
  return isChatDockedRight && !isChatFullScreen;
}

/**
 * Header participants line ("Just you" / "3 people").
 *
 * Docked: "JUST YOU" is NOISE when solo — the line renders ONLY for group
 * threads ("3 people"). A routine thread's single meta line (glyph +
 * compact schedule + pause/resume chip) owns line 2 in the docked two-line
 * header, so a routine thread shows no participants line there either
 * (membership stays visible in fullscreen and on the sidebar/chip glyphs).
 *
 * Fullscreen/floating: current behavior — the line always renders.
 */
export function shouldShowParticipantsLine(args: {
  docked: boolean;
  participantCount: number;
  isRoutineThread: boolean;
}): boolean {
  if (!args.docked) return true;
  if (args.isRoutineThread) return false;
  return isGroupParticipantCount(args.participantCount);
}

/**
 * Bottom thread chip strip: with fewer than 2 threads there is NOTHING to
 * switch, so the docked panel hides the strip entirely and gives the room
 * back to the conversation + composer. New-thread creation stays reachable
 * through the header's docked overflow menu ("new thread" item — see
 * ThreadHeaderOverflowMenu), so no functionality is lost.
 *
 * PHONES: never (2026-09-13). Owner report: "Switching between threads in
 * mobile view quickly should be easy." The strip was the opposite of quick —
 * a horizontally-scrolling rail showing about one and a half chips on a 390px
 * screen, so reaching the third thread meant a sideways drag on a surface
 * whose neighbours all scroll vertically. It cost ~48px of a transcript that
 * was already getting less than half the screen, permanently, to show one
 * neighbour.
 *
 * The header takes the job instead: the thread title opens the full list, and
 * the list is the SAME ThreadListSheet the strip's own overflow trigger has
 * opened since 2026-08-14 — vertical, finger-sized, searchable, with rename
 * and new-thread on each row. This is the docked argument applied one screen
 * down: the surface goes away, every affordance it carried stays reachable.
 *
 * The floating pane on a desktop keeps the strip exactly as before — it is
 * still the only thread surface there.
 */
export function shouldShowThreadStrip(args: {
  docked: boolean;
  threadCount: number;
  /** Phone-sized viewport, where the header owns thread switching. */
  phone?: boolean;
}): boolean {
  if (args.phone) return false;
  if (!args.docked) return true;
  return args.threadCount >= 2;
}

/**
 * Message-footer timestamp (owner feedback, Miles 2026-08-01): in the docked
 * panel the meta row ("Prest0n" chip + "08/01/2026 04:21 PM" + copy/info
 * icons) wrapped — the close-up screenshot showed "Prest0n" breaking
 * MID-NAME. The row is now nowrap (see MessageTime in styles.ts + the sender
 * chip's nowrap label), so the timestamp must earn its width back at docked
 * density.
 *
 * THE PINNED RULE — docked:
 *   • message sent TODAY (same local calendar day as `now`)
 *       → time only, natural hour: "4:21 PM"
 *   • any other day
 *       → "08/01 · 4:21 PM" (MM/DD, no year, mid-dot separator)
 *   and the FULL `formatMessageTime` string rides along as the `title`
 *   tooltip so nothing is lost.
 * Fullscreen/floating: unchanged — the full "08/01/2026 04:21 PM" label with
 * the existing generic "time sent" tooltip.
 *
 * Pure (mode + timestamp + injectable `now` for tests); the render site in
 * GlobalChatSystem.tsx consults exactly this helper.
 */
export function formatMessageMetaTimestamp(args: {
  docked: boolean;
  timestamp: Date | string;
  now?: Date;
}): { label: string; title: string } {
  const full = formatMessageTime(args.timestamp);
  if (!args.docked) {
    return { label: full, title: 'time sent' };
  }
  const date = args.timestamp instanceof Date ? args.timestamp : new Date(args.timestamp);
  const now = args.now ?? new Date();
  // Natural-hour time ("4:21:07 PM", not "04:21:07 PM") — the compact form
  // the directive names; minutes and seconds stay two-digit (seconds always,
  // Miles 2026-09-09).
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (isToday) {
    return { label: time, title: full };
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return { label: `${month}/${day} · ${time}`, title: full };
}
